/**
 * Catalog rules — PURE. Every input is passed in: no store, no `window`, no clock,
 * no `isOffline()`. See `./types` for why that is the whole offline-readiness plan.
 *
 * Ported from the POS's catalog path (`apps/web/src/helper/catalogueHelpers.js` +
 * `Containers/NewOrder`), with three deliberate changes:
 *
 *  1. `window.RL.catalogutil` → `@dotpe/rista-library/lib/sale/catalog_util`. Same code,
 *     but a module import instead of a `<script>` global, which is what lets this run
 *     under React Native at all.
 *  2. `structuredClone` → `lodash/cloneDeep`. Hermes has no `structuredClone`;
 *     apps/mobile polyfills it at `index.js:4`, and shared code that depends on a host's
 *     bootstrap order breaks silently — worse, node has it natively, so tests would pass.
 *  3. `displayOrder` sorts with `?? Infinity`, not `|| Infinity`. The POS's version sends
 *     `displayOrder: 0` to the END of the list.
 */
/// <reference path="../types/rista-library.d.ts" />
// ^ NOT redundant: each app's tsconfig only `include`s its own src/, so the ambient
//   declaration has to travel with the file that needs it or every consumer app trips
//   TS7016 on the untyped library.
import cloneDeep from "lodash/cloneDeep";
import { tz } from "moment-timezone";
import type {
  CardBuilder,
  CatalogItem,
  CatalogNode,
  CategoryData,
  CategoryRef,
  ChannelCatalog,
  DietaryTag,
  RecommendedGroups,
  ResolveContext,
  ResolveOptions,
  TagPriority,
} from "./types";

// rista-library ships untyped JS; the cast is the house pattern for it.
import catalogUtilImport from "@dotpe/rista-library/lib/sale/catalog_util";
const catalogUtil = catalogUtilImport as any;

/**
 * Module-private on purpose. `apps/web/src/data/constants.ts` already exports BOTH an
 * `ITEM_TYPES` (this vocabulary) and an unrelated `ITEM_TYPE` (`item`/`category`/`optionset`,
 * for label settings), so exporting either name from core would collide with one of them —
 * audit check A. The sibling ordering domains take the PREDICATES below instead, which is a
 * better surface anyway: a caller asks a question rather than comparing a string.
 */
const ITEM_TYPE = { variant: "Variant", group: "Group" } as const;

/** Is this item one concrete variant of a Group? */
export function isVariantItem(item: any): boolean {
  return item?.type === ITEM_TYPE.variant;
}

/** Is this item a Group — a card that resolves to a variant once attributes are chosen? */
export function isGroupItem(item: any): boolean {
  return item?.type === ITEM_TYPE.group;
}

/** Branch-local time for price and schedule resolution. The only clock read here. */
export function branchTimeOf(zoneId?: string, now?: Date): string {
  return zoneId ? tz(now ?? new Date(), zoneId).format() : (now ?? new Date()).toISOString();
}

/**
 * Items and option sets active for this branch + channel.
 *
 * `getActiveItemsAndOptionSetsForBranchAndChannel` MUTATES its argument in place and
 * returns nothing — it writes `params.items` and `params.optionSets` back. Cloning here
 * is what contains that: callers pass store data and get new arrays.
 *
 * NOTE: the POS discards the filtered `optionSets` (it re-derives them separately) even
 * though the library removes deactivated options for the branch/channel here. This
 * returns both; the customiser should use these rather than re-deriving.
 */
export function activeItemsForChannel(
  items: any[],
  optionSets: any[],
  ctx: { branchId: string | undefined; channelDefId: string },
): { items: any[]; optionSets: any[] } {
  const params = {
    items: cloneDeep(items ?? []),
    optionSets: cloneDeep(optionSets ?? []),
    branchId: ctx.branchId,
    channelDefId: ctx.channelDefId,
  };
  catalogUtil?.getActiveItemsAndOptionSetsForBranchAndChannel?.(params);
  return { items: params.items ?? [], optionSets: params.optionSets ?? [] };
}

/**
 * categoryId → item ids, for one channel. Feed it the output of `activeItemsForChannel`.
 *
 * Variants are skipped: a variant is reachable through its parent's customiser, never as
 * its own card. An item with no `categoryId` has nowhere to render and is dropped.
 */
export function buildChannelCatalog(items: any[], channelId: string): ChannelCatalog {
  const catalog: ChannelCatalog = {};
  if (!channelId) {
    return catalog;
  }

  for (const item of items ?? []) {
    if (item?.type === ITEM_TYPE.variant) continue;
    if (!item?.channelIds?.includes(channelId)) continue;

    const categoryId = item?.info?.categoryId;
    if (!categoryId) continue;

    const node = (catalog[categoryId] ??= { items: [], parentCatItems: [], subCats: {} });
    const subCategoryId = item?.info?.subCategoryId;

    if (subCategoryId) {
      (node.subCats[subCategoryId] ??= { items: [] }).items.push(item.mid);
    } else {
      node.parentCatItems.push(item.mid);
    }
    node.items.push(item.mid);
  }

  return catalog;
}

/**
 * A channel's catalogue narrowed to one MENU: the items whose `info.menuIds` list it, with every
 * category and subcategory left empty dropped. A port of web v1's `populateMenuChannel`
 * (`helper/catalogueHelpers.ts`, V1_DUPLICATES.md), memoised per channel and menu by
 * `prepareMenuCatalog` beside the channel catalogue, so the two are invalidated together.
 */
export function menuCatalog(
  catalog: ChannelCatalog,
  itemsById: Record<string, any>,
  menuId: string,
): ChannelCatalog {
  const inMenu = (id: string) => Boolean(itemsById?.[id]?.info?.menuIds?.includes(menuId));
  const narrowed: ChannelCatalog = {};
  for (const [categoryId, node] of Object.entries(catalog ?? {})) {
    const subCats: CatalogNode["subCats"] = {};
    const subItems: string[] = [];
    for (const [subId, sub] of Object.entries(node.subCats ?? {})) {
      const items = (sub.items ?? []).filter(inMenu);
      if (!items.length) continue;
      subCats[subId] = { items };
      subItems.push(...items);
    }
    const parentCatItems = (node.parentCatItems ?? []).filter(inMenu);
    const items = [...parentCatItems, ...subItems];
    if (items.length) narrowed[categoryId] = { items, parentCatItems, subCats };
  }
  return narrowed;
}

/** Ascending by `displayOrder`; anything without one keeps its incoming order, last. */
function byDisplayOrder<T>(list: T[], orderOf: (entry: T) => number | undefined): T[] {
  return list
    .map((entry, index) => ({ entry, index, order: orderOf(entry) ?? Infinity }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ entry }) => entry);
}

/** The category rail, in display order, named. Categories with no items never appear. */
export function orderedCategories(
  catalog: ChannelCatalog,
  categoryData: CategoryData,
): CategoryRef[] {
  const ids = Object.keys(catalog).filter((id) => catalog[id]?.items?.length);
  return byDisplayOrder(ids, (id) => categoryData?.[id]?.displayOrder).map((id) => ({
    id,
    name: categoryData?.[id]?.name ?? "",
    // `imageDataId`, not a URL: the catalog stores an image ID and the UI resolves it with
    // `getImageUrl`. A category rail with tiles needs it; a text-only rail ignores it.
    imageId: categoryData?.[id]?.imageDataId,
  }));
}

/** Subcategories of one category, in display order — only those holding an item. */
export function subCategoriesOf(
  catalog: ChannelCatalog,
  categoryData: CategoryData,
  categoryId: string,
): CategoryRef[] {
  const present = catalog?.[categoryId]?.subCats ?? {};
  const declared = categoryData?.[categoryId]?.subCategories ?? [];
  const withItems = declared.filter((sub) => present[sub.mid]?.items?.length);
  return byDisplayOrder(withItems, (sub) => sub.displayOrder).map((sub) => ({
    id: sub.mid,
    name: sub.name ?? "",
  }));
}

/**
 * One category, laid out the way it is rendered: the items sitting directly on it, then each
 * subcategory that has any, in display order.
 *
 * This is the shape a `SectionList` takes, and building it here rather than in the screen is
 * what stops two platforms from ordering the same menu differently. Empty sections are absent
 * rather than rendered with a header and nothing under it.
 */
export function categoryView(
  catalog: ChannelCatalog,
  categoryData: CategoryData,
  categoryId: string,
): { subCategories: CategoryRef[]; sections: { id: string; name: string; itemIds: string[] }[] } {
  const node = catalog?.[categoryId];
  if (!node) return { subCategories: [], sections: [] };

  const subCategories = subCategoriesOf(catalog, categoryData, categoryId);
  const sections: { id: string; name: string; itemIds: string[] }[] = [];

  if (node.parentCatItems?.length) {
    // No header of its own: these items belong to the category the customer just tapped.
    sections.push({ id: categoryId, name: "", itemIds: node.parentCatItems });
  }
  for (const sub of subCategories) {
    const itemIds = node.subCats?.[sub.id]?.items ?? [];
    if (itemIds.length) sections.push({ id: sub.id, name: sub.name, itemIds });
  }

  return { subCategories, sections };
}

/**
 * veg / nveg / egg for an item, by tag id. Port of the POS's `provideItemTag`.
 *
 * Priority is `[nveg, egg, veg]` and it matters: an item tagged both non-veg and egg is
 * non-veg. There is no `isVeg` field anywhere in the catalog — tags are entities matched
 * by name upstream, which is where `tagPriority` comes from.
 */
export function dietaryTagOf(
  tagIds: (string | number)[] | undefined,
  tagPriority: TagPriority | undefined,
): DietaryTag | null {
  if (!tagIds?.length || !tagPriority?.length) {
    return null;
  }
  if (tagPriority[0] != null && tagIds.includes(tagPriority[0])) return "nveg";
  if (tagPriority[1] != null && tagIds.includes(tagPriority[1])) return "egg";
  if (tagPriority[2] != null && tagIds.includes(tagPriority[2])) return "veg";
  return null;
}

/**
 * The tag priority `dietaryTagOf` reads, from the merchant's item tags keyed by id: the ids named
 * non-vegetarian, egg and vegetarian (by name, any case), in that order, `-1` where none is set.
 * v2's copy of v1's `setPriorityForItemTags` (V1_DUPLICATES.md D68).
 */
const TAG_SLOTS: Record<string, number> = { "non-vegetarian": 0, egg: 1, vegetarian: 2 };
export function tagPriorityOf(itemTags: Record<string, { name?: string }> | undefined): (string | number)[] {
  const priority: (string | number)[] = [-1, -1, -1];
  for (const id of Object.keys(itemTags ?? {})) {
    const slot = TAG_SLOTS[itemTags?.[id]?.name?.toLowerCase() ?? ""];
    if (slot !== undefined) priority[slot] = id;
  }
  return priority;
}

/** Is the item orderable right now, per its schedule? A group needs ONE live variant. */
export function isItemAvailableNow(itemId: string, ctx: ResolveContext): boolean {
  const item = ctx.itemsById?.[itemId];
  if (!item) return false;

  const check = (candidate: any) =>
    Boolean(
      catalogUtil?.isItemAvailable?.({
        item: candidate,
        branchTime: ctx.branchTime,
        channelDefId: ctx.channel?.channelDefId,
        itemSchedules: ctx.itemSchedules,
        // DELIBERATE DIVERGENCE: the POS omits branchId here (helper/catalogueHelpers.js
        // :1102), so `isScheduleValidForBranchAndChannel` short-circuits to true and
        // another branch's schedule can time-gate this branch's menu. Pricing already
        // passes it. An item scheduled only for other branches now reads as unscheduled
        // — available — rather than following foreign hours.
        branchId: ctx.branchId,
      }),
    );

  if (item.type === ITEM_TYPE.group) {
    return (item.variantIds ?? []).some((id: string) => {
      const variant = ctx.itemsById?.[id];
      return variant ? check(variant) : false;
    });
  }
  return check(item);
}

/** Price for this item on this channel, at this branch, at `ctx.branchTime`. */
export function priceOf(item: any, ctx: ResolveContext): number {
  const price = catalogUtil?.getItemPrice?.({
    item,
    branchTime: ctx.branchTime,
    branchId: ctx.branchId,
    channelDefId: ctx.channel?.channelDefId,
    groupId: ctx.channel?.groupId,
    itemSchedules: ctx.itemSchedules,
  });
  return typeof price === "number" ? price : NaN;
}

/**
 * The name a card shows.
 *
 * A VARIANT gets its attributes appended — "Biryani Large" rather than a second card called
 * "Biryani". The POS builds this string in three different places and only for a direct add
 * (`getVariantLongName` in `CustomiseItemPopup`, `getItemAttributes` in `BarCodeSearch`, and
 * the barcode index), so the same variant reads differently depending on where it appears.
 * Groups and simple items are unaffected — they carry no `variantValues`.
 */
export function displayNameOf(item: any): string {
  const base = item?.info?.longName ?? item?.info?.shortName ?? "";
  if (!isVariantItem(item)) return base;
  const attributes = attributeSummary(item?.variantValues);
  return attributes ? `${base} ${attributes}`.trim() : base;
}

/** "Large Thin" — a variant's attribute values, joined. The POS's `getItemAttributes`. */
export function attributeSummary(variantValues: any[] | undefined): string {
  return (variantValues ?? [])
    .map((value: any) => value?.value)
    .filter(Boolean)
    .join(" ")
    .trim();
}

/** One raw item → one card. */
export function toCatalogItem(item: any, ctx: ResolveContext, soldOut: Set<string>): CatalogItem {
  return {
    id: item.mid,
    name: displayNameOf(item),
    price: priceOf(item, ctx),
    // An image ID, not a URL. `info.imageURL` is carried alongside it on the raw item —
    // mobile's parser copies both into its base map — but NOTHING in either app ever
    // renders it: every image path goes `imageDataId` -> `getImageUrl(id, size)`. Reading
    // the URL field produced a card whose image never loaded.
    imageId: item?.info?.imageDataId,
    tag: dietaryTagOf(item?.info?.tagIds, ctx.tagPriority),
    isCustomisable: Boolean(item?.variantIds?.length || item?.priceInfo?.optionSetIds?.length),
    isSoldOut: soldOut.has(item.mid),
    categoryId: item?.info?.categoryId,
    subCategoryId: item?.info?.subCategoryId,
    displayOrder: item?.displayOrder,
  };
}

/**
 * Item ids → cards, in display order, with the optional passes applied.
 *
 * This is the ONLY place a raw item becomes render data, so a screen never holds two
 * shapes of the same item. Nothing is dropped unless an option asks for it: by default a
 * sold-out item comes back flagged, not missing, which is the POS's behaviour — web's
 * `ItemsView` labels off `soldOutItemsList` and never drops a row.
 *
 * `excludeSoldOuts` is what CUSTOMER-facing flows pass, through `CUSTOMER_MENU_RESOLVE`: those
 * hide an out-of-stock item rather than labelling it, because a customer has nobody to ask.
 * ⚠️ It filters against `ctx.soldOutItemIds`, so it does nothing at all unless that list was
 * actually fetched — see `CUSTOMER_MENU_RESOLVE` and `soldOut/shared.ts`.
 */
export function resolveItems<T = CatalogItem>(
  itemIds: string[],
  ctx: ResolveContext,
  options: ResolveOptions = {},
  toCard: CardBuilder<T> = toCatalogItem as unknown as CardBuilder<T>,
): T[] {
  const soldOut = new Set(ctx.soldOutItemIds ?? []);
  const seen = new Set<string>();
  const resolved: T[] = [];

  for (const itemId of itemIds ?? []) {
    let item = ctx.itemsById?.[itemId];
    if (!item) continue;

    const isVariant = item.type === ITEM_TYPE.variant;
    if (options.skipVariants && !options.resolveVariantToParent && isVariant) continue;
    if (options.channelEligibility && !item.channelIds?.includes(ctx.channel?.channelId)) continue;
    if (options.excludeSoldOuts && soldOut.has(itemId)) continue;

    if (options.resolveVariantToParent && isVariant) {
      item = ctx.itemsById?.[item.parentItemId];
    }
    if (!item || seen.has(item.mid)) continue;
    seen.add(item.mid);

    // Schedule is checked against the id we were GIVEN: a group resolves through its
    // variants, and asking about the parent after resolution would answer a different
    // question than "can the customer order this right now".
    if (options.scheduleAvailability && !isItemAvailableNow(itemId, ctx)) continue;

    const card = toCard(item, ctx, soldOut);
    if (options.validPrice && Number.isNaN((card as any)?.price)) continue;

    resolved.push(card);
  }

  // `"input"` short-circuits the sort entirely rather than sorting by a key that happens to be
  // stable — a card shape need not carry `displayOrder` at all.
  if (options.sort === "input") return resolved;
  return byDisplayOrder(resolved, (card) => (card as any)?.displayOrder);
}

/**
 * The BASELINE every customer-facing menu shares: hide what the kitchen cannot make right now
 * (out of schedule), what has no price on this channel, and what this channel does not carry.
 *
 * 🔴 **All three hide things NOBODY could order, which is why they are audience-wide and not a
 * per-flow choice.** An item with no price on this channel is unorderable by any customer on any
 * surface; there is nothing for a flow to decide. `posCard`'s `DEFAULT_FILTER_CONFIG` is the
 * staff-facing answer and filters nothing, because a cashier is meant to see everything.
 *
 * ⚠️ **SOLD-OUT IS DELIBERATELY NOT HERE.** A sold-out item is real and normally orderable, so
 * whether to hide it or label it is a presentation choice a FLOW makes — and two customer-facing
 * flows may legitimately answer differently. Each owns its own policy and composes it on top:
 *
 * ```ts
 * export const KIOSK_MENU_RESOLVE = Object.freeze({
 *   ...CUSTOMER_MENU_RESOLVE,
 *   excludeSoldOuts: KIOSK_HIDES_SOLD_OUT,
 * });
 * ```
 *
 * `kiosk/catalog/menuPolicy` is the worked example and the only one today. Putting the answer
 * HERE instead — which is how it first shipped — makes one flow's product decision reach every
 * present and future consumer of this constant, including the POS the moment it adopts these
 * hooks.
 *
 * ONE definition of the baseline. It was a private `DEFAULT_RESOLVE` literal in both
 * `useCatalogSections` and `useCatalogSearch`, and the kiosk's rail needs the very same answer —
 * a third copy is how a rail that counts one thing ends up beside a grid that shows another
 * (`hasOrderableItem`). Frozen, because every caller shares it.
 *
 * ⚠️ It is a PRESET a caller opts into, never a default. Those two hooks used to default to it,
 * which silently gave every future POS caller a customer's menu; they filter nothing when
 * `resolve` is omitted.
 */
export const CUSTOMER_MENU_RESOLVE: ResolveOptions = Object.freeze({
  scheduleAvailability: true,
  validPrice: true,
  channelEligibility: true,
});

/**
 * Does at least one of these items resolve under `options` — i.e. would a grid built from them
 * show anything at all?
 *
 * 🔴 **It IS `resolveItems`, one id at a time, not a re-derivation of its checks.** The kiosk's
 * rail and subcategory chips used to count RAW items while its grid resolved them, so a category
 * whose every item was out of schedule — breakfast at dinner time — stayed in the rail, opened
 * onto "No items available", and, being first in display order, was where the session LANDED.
 * Asking the grid's own function is what makes "the rail offers it" and "the grid shows it" the
 * same statement.
 *
 * Stops at the first orderable item, so the common case — a category whose first item is
 * available — costs one resolve, not one per item. The worst case is a category with nothing to
 * order, which costs its length, once per memo.
 */
export function hasOrderableItem(
  itemIds: string[] | undefined,
  ctx: ResolveContext,
  options: ResolveOptions,
): boolean {
  for (const itemId of itemIds ?? []) {
    if (resolveItems([itemId], ctx, { ...options, sort: "input" }).length > 0) return true;
  }
  return false;
}

/** `entries` narrowed to those with something to order, in their original order. */
export function onlyOrderable<T>(
  entries: T[],
  itemIdsOf: (entry: T) => string[] | undefined,
  ctx: ResolveContext,
  options: ResolveOptions,
): T[] {
  return entries.filter((entry) => hasOrderableItem(itemIdsOf(entry), ctx, options));
}

/**
 * The id of the "Chef Recommended" tag, matched by NAME — the same lookup the POS does
 * (`findMidOfChefRecommended`). Tags are catalog entities with no stable id per merchant.
 *
 * `never[]` is in the parameter type because `catalogStore.itemTags` genuinely holds `[]`
 * until `getItemTagSuccess` lands and a `mid`-keyed map afterwards (see that field's note in
 * `catalogueSlice/shared.ts`). `Object.entries` handles both, so the empty case needs no
 * branch here — it yields no entries and falls through to `undefined`.
 */
export function chefRecommendedTagId(
  itemTags: Record<string, { mid?: string; name?: string }> | never[] | undefined,
  tagName = "Chef Recommended",
): string | undefined {
  for (const [id, tag] of Object.entries(itemTags ?? {})) {
    if (tag?.name === tagName) {
      return tag.mid ?? id;
    }
  }
  return undefined;
}

/**
 * The synthetic "Recommended" category the POS shows first: chef-recommended items by
 * tag, and the branch's most-sold, both narrowed to what this channel actually carries.
 */
export function recommendedGroups(params: {
  catalog: ChannelCatalog;
  itemsById: Record<string, any>;
  chefRecommendedTagId?: string;
  mostSoldItemIds?: string[];
}): RecommendedGroups {
  const inChannel = new Set<string>();
  for (const node of Object.values(params.catalog ?? {})) {
    for (const id of node.items ?? []) inChannel.add(id);
  }

  const chefRecommended = params.chefRecommendedTagId
    ? [...inChannel].filter((id) =>
        params.itemsById?.[id]?.info?.tagIds?.includes(params.chefRecommendedTagId),
      )
    : [];

  const mostSold: string[] = [];
  const seen = new Set<string>();
  for (const id of params.mostSoldItemIds ?? []) {
    if (inChannel.has(id) && !seen.has(id)) {
      seen.add(id);
      mostSold.push(id);
    }
  }

  return { chefRecommended, mostSold };
}

/**
 * The synthetic category's ids. They are plain strings rather than mids because nothing
 * upstream issues one: the category does not exist in the merchant's catalogue.
 *
 * `RECOMMENDED_CATEGORY_ID` is the POS's own literal (`RECOMMENDED_ITEMS_CATEGORY` in
 * `utils/constants`), kept identical so a stored selection means the same thing on both
 * surfaces. The section ids are NOT: the POS uses the chef-recommended TAG's mid for one of
 * its two subcategories, which differs per merchant and is `undefined` until `itemTags`
 * lands — an id that can change under a stored selection is not an id. Both are fixed here.
 */
export const RECOMMENDED_CATEGORY_ID = "recommendeditems";
export const MOST_SOLD_SECTION_ID = "mostsolditems";
export const CHEF_RECOMMENDED_SECTION_ID = "chefrecommendeditems";

/**
 * What the rail and the chips are labelled. Customer-facing, so they are shorter than the
 * POS's cashier-facing strings ("Most sold Items", "Chef Recommended Items") — the words
 * are the same, the sentence-cased padding is not.
 */
const RECOMMENDED_CATEGORY_NAME = "Recommended";
const MOST_SOLD_SECTION_NAME = "Most Sold";
const CHEF_RECOMMENDED_SECTION_NAME = "Chef Recommended";

/**
 * The synthetic "Recommended" category, laid out like `categoryView` lays out a real one:
 * most-sold first, then chef-recommended, each present only when it holds something.
 *
 * 🔴 **The ids come back in FINAL RENDER ORDER, and the caller must resolve them with
 * `sort: "input"`** — `useCategoryCatalog` returns `sectionSort` so a screen cannot forget.
 * `mostSold` is a RANKING (`analytics/item/frequent`, best-selling first) and re-sorting it by
 * menu `displayOrder` would throw that away, which `ResolveOptions.sort` calls a correctness
 * failure rather than a cosmetic one. Chef-recommended has no ranking of its own, so it is
 * sorted into menu order HERE instead of relying on the resolve to do it.
 *
 * The two sections deliberately still OVERLAP: a top seller the chef also recommends belongs
 * under both chips, and each chip has to keep its promise on its own. The de-duplication that
 * the merged, chipless grid needs happens where the merge happens — `useCatalogSections`.
 *
 * The POS's version of this ordering is accidental: it sorts both of its lists by
 * `displayOrder` in `ItemsView`, so its "Most sold" tab is in menu order too.
 */
export function recommendedCategoryView(
  groups: RecommendedGroups | undefined,
  itemsById?: Record<string, any>,
): { subCategories: CategoryRef[]; sections: { id: string; name: string; itemIds: string[] }[] } {
  const chefRecommended = byDisplayOrder(
    groups?.chefRecommended ?? [],
    (id) => itemsById?.[id]?.displayOrder,
  );

  const candidates = [
    { id: MOST_SOLD_SECTION_ID, name: MOST_SOLD_SECTION_NAME, itemIds: groups?.mostSold ?? [] },
    { id: CHEF_RECOMMENDED_SECTION_ID, name: CHEF_RECOMMENDED_SECTION_NAME, itemIds: chefRecommended },
  ];

  const sections = candidates.filter((section) => section.itemIds.length);
  return {
    subCategories: sections.map(({ id, name }) => ({ id, name, synthetic: true })),
    sections,
  };
}

/**
 * The rail with "Recommended" on top — opt-in, because the category is a PRODUCT decision
 * rather than a property of the catalogue. The POS shows it only online and only under its
 * large-catalogue threshold; the kiosk shows it whenever there is something in it.
 *
 * An empty one is absent rather than empty, which is `orderedCategories`' rule for real
 * categories too and is the whole offline story here: a device with no `mostSoldItems` fetch
 * and no `itemTags` yet produces two empty groups, so the row simply never appears. There is
 * no `isOffline` branch to get wrong.
 */
export function withRecommendedCategory(
  categories: CategoryRef[],
  groups: RecommendedGroups | undefined,
): CategoryRef[] {
  if (!groups?.chefRecommended?.length && !groups?.mostSold?.length) return categories;
  return [
    { id: RECOMMENDED_CATEGORY_ID, name: RECOMMENDED_CATEGORY_NAME, synthetic: true },
    ...categories,
  ];
}
